/**
 * 이슈 게시판 서비스 — vsmr.issue.v1 Connect 클라이언트 래퍼.
 *
 * 베타 피드백 창구 (GitHub 이슈 게시판 참조 모델). 가시성(일반=자기 것만,
 * admin=전체)과 인가는 BFF 가 강제하고, 여기서는 타입 매핑만 한다.
 */

import { createPromiseClient } from "@connectrpc/connect";
import type { PromiseClient } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { IssueService } from "@/stubs/issue/issue_connect";
import {
  AddIssueCommentRequest,
  CreateIssueRequest,
  DeleteIssueCommentRequest,
  GetIssueAttachmentPolicyRequest,
  GetIssueAttachmentRequest,
  GetIssueRequest,
  IssueOrigin,
  ListIssuesRequest,
  UpdateIssueCommentRequest,
  UpdateIssueMetaRequest,
  UpdateIssueRequest,
  UpdateIssueStateRequest,
  UploadIssueAttachmentRequest,
  type Issue as PbIssue,
  type IssueAttachment as PbIssueAttachment,
  type IssueComment as PbIssueComment,
} from "@/stubs/issue/issue_pb";
import { BFF_URL, createAuthInterceptor } from "@/app/services/connectCommon";

const transport = createConnectTransport({
  baseUrl: `${BFF_URL}/api`,
  interceptors: [createAuthInterceptor()],
});

const issueClient: PromiseClient<typeof IssueService> = createPromiseClient(
  IssueService,
  transport,
);

export type IssueState = "open" | "closed";
export type IssueCloseReason = "completed" | "not_planned";

export interface IssueItem {
  id: string;
  number: number;
  authorId: string;
  authorName: string;
  title: string;
  body: string;
  state: string;
  stateReason: string;
  labels: string[];
  assigneeId: string;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  /** 제목·본문을 마지막으로 편집한 시각 — 편집한 적 없으면 null */
  editedAt: string | null;
  /** 삭제된 댓글은 세지 않는다(서버 계약) */
  commentCount: number;
}

export interface IssueCommentItem {
  id: string;
  issueId: string;
  authorId: string;
  authorName: string;
  /** 삭제된 댓글은 빈 문자열 — 화면은 자리 표시를 그린다 */
  body: string;
  createdAt: string;
  /** 본문을 마지막으로 편집한 시각 — 편집한 적 없으면 null */
  editedAt: string | null;
  /** 소프트 삭제됨 — 목록에 남되 본문이 없다 */
  deleted: boolean;
  /** 지운 사람(작성자 본인 또는 admin). 삭제되지 않았으면 빈 값 */
  deletedBy: string;
}

function tsToIso(ts: { seconds?: bigint | number | string } | undefined): string | null {
  if (!ts || !ts.seconds) return null;
  const ms = Number(ts.seconds) * 1000;
  return ms > 0 ? new Date(ms).toISOString() : null;
}

function toIssue(pb: PbIssue): IssueItem {
  return {
    id: pb.id,
    number: Number(pb.number),
    authorId: pb.authorId,
    authorName: pb.authorName,
    title: pb.title,
    body: pb.body,
    state: pb.state,
    stateReason: pb.stateReason,
    labels: [...pb.labels],
    assigneeId: pb.assigneeId,
    createdAt: tsToIso(pb.createdAt) ?? new Date().toISOString(),
    updatedAt: tsToIso(pb.updatedAt) ?? new Date().toISOString(),
    closedAt: tsToIso(pb.closedAt),
    editedAt: tsToIso(pb.editedAt),
    commentCount: pb.commentCount,
  };
}

function toComment(pb: PbIssueComment): IssueCommentItem {
  return {
    id: pb.id,
    issueId: pb.issueId,
    authorId: pb.authorId,
    authorName: pb.authorName,
    body: pb.body,
    createdAt: tsToIso(pb.createdAt) ?? new Date().toISOString(),
    editedAt: tsToIso(pb.editedAt),
    deleted: pb.deleted,
    deletedBy: pb.deletedBy,
  };
}

export async function createIssue(title: string, body: string): Promise<IssueItem> {
  const response = await issueClient.createIssue(new CreateIssueRequest({ title, body, origin: IssueOrigin.SIM_WEB }));
  if (!response.issue) throw new Error("empty issue response");
  return toIssue(response.issue);
}

export async function listIssues(options: {
  state?: IssueState;
  allUsers?: boolean;
  pageSize?: number;
  pageToken?: string;
} = {}): Promise<{ issues: IssueItem[]; nextPageToken: string }> {
  const response = await issueClient.listIssues(
    new ListIssuesRequest({
      state: options.state ?? "",
      allUsers: options.allUsers ?? false,
      pageSize: options.pageSize ?? 30,
      pageToken: options.pageToken ?? "",
    }),
  );
  return {
    issues: response.issues.map(toIssue),
    nextPageToken: response.nextPageToken,
  };
}

export async function getIssue(
  id: string,
): Promise<{ issue: IssueItem; comments: IssueCommentItem[] }> {
  const response = await issueClient.getIssue(new GetIssueRequest({ id }));
  if (!response.issue) throw new Error("issue not found");
  return { issue: toIssue(response.issue), comments: response.comments.map(toComment) };
}

export async function addIssueComment(issueId: string, body: string): Promise<IssueCommentItem> {
  const response = await issueClient.addIssueComment(
    new AddIssueCommentRequest({ issueId, body }),
  );
  if (!response.comment) throw new Error("empty comment response");
  return toComment(response.comment);
}

/** 제목·본문 편집 — 작성자만(admin 도 남의 글은 못 고친다). 서버가 강제, 여기는 매핑. */
export async function updateIssue(issueId: string, title: string, body: string): Promise<IssueItem> {
  const response = await issueClient.updateIssue(new UpdateIssueRequest({ issueId, title, body }));
  if (!response.issue) throw new Error("empty issue response");
  return toIssue(response.issue);
}

/** 댓글 본문 편집 — 댓글 작성자만. */
export async function updateIssueComment(
  issueId: string,
  commentId: string,
  body: string,
): Promise<IssueCommentItem> {
  const response = await issueClient.updateIssueComment(
    new UpdateIssueCommentRequest({ issueId, commentId, body }),
  );
  if (!response.comment) throw new Error("empty comment response");
  return toComment(response.comment);
}

/** 댓글 소프트 삭제 — 댓글 작성자 또는 admin. 자리 표시(deleted=true)가 돌아온다. */
export async function deleteIssueComment(
  issueId: string,
  commentId: string,
): Promise<IssueCommentItem> {
  const response = await issueClient.deleteIssueComment(
    new DeleteIssueCommentRequest({ issueId, commentId }),
  );
  if (!response.comment) throw new Error("empty comment response");
  return toComment(response.comment);
}

export async function updateIssueState(
  issueId: string,
  state: IssueState,
  stateReason?: IssueCloseReason,
): Promise<IssueItem> {
  const response = await issueClient.updateIssueState(
    new UpdateIssueStateRequest({ issueId, state, stateReason: stateReason ?? "" }),
  );
  if (!response.issue) throw new Error("empty issue response");
  return toIssue(response.issue);
}

export async function updateIssueLabels(issueId: string, labels: string[]): Promise<IssueItem> {
  const response = await issueClient.updateIssueMeta(
    // setLabels 존재 플래그 — proto3 repeated 는 생략과 전부-삭제를 구분하지 못한다
    new UpdateIssueMetaRequest({ issueId, labels, setLabels: true }),
  );
  if (!response.issue) throw new Error("empty issue response");
  return toIssue(response.issue);
}

// ------------------------------------------------------------
// 첨부(스크린샷) — 본문은 `vsmr-attachment://{id}` 의사 URL 로 참조하고,
// 렌더러가 GetIssueAttachment 로 바이트를 받아 blob URL 로 해석한다.
// BFF 가 이슈 가시성(작성자·admin)으로 접근을 강제하므로 URL 직링크가 없다.
// ------------------------------------------------------------

export const ATTACHMENT_URL_SCHEME = "vsmr-attachment://";

export interface IssueAttachmentItem {
  id: string;
  fileName: string;
  contentType: string;
  size: number;
}

function toAttachment(pb: PbIssueAttachment): IssueAttachmentItem {
  return {
    id: pb.id,
    fileName: pb.fileName,
    contentType: pb.contentType,
    size: Number(pb.size),
  };
}

/** 본문에 삽입할 첨부 참조 마크다운 — alt 의 구문 문자는 파손 방지 치환 */
export function attachmentMarkdown(fileName: string, id: string): string {
  const alt = (fileName || "image").replace(/[[\]()]/g, "_");
  return `![${alt}](${ATTACHMENT_URL_SCHEME}${id})`;
}

export async function uploadIssueAttachment(
  fileName: string,
  contentType: string,
  content: Uint8Array<ArrayBuffer>,
): Promise<IssueAttachmentItem> {
  const response = await issueClient.uploadIssueAttachment(
    new UploadIssueAttachmentRequest({ fileName, contentType, content }),
  );
  if (!response.attachment) throw new Error("empty attachment response");
  return toAttachment(response.attachment);
}

// 받는 첨부 형식은 배포마다 고정이다 — 성공한 답만 모듈에 둔다(실패는 다음에 다시 묻는다)
let attachmentPolicyPromise: Promise<readonly string[]> | null = null;

/**
 * 서버가 받는 첨부 형식(MIME) — 업로드가 거절하는 목록 그대로다(선언은 BFF 한 곳). 파일을 고를 때 이것으로 미리 판정한다.
 *
 * @throws 답을 얻지 못했을 때 — 호출측은 «모름» 으로 두고 판정을 서버의 거절에 맡긴다
 */
export function fetchAttachmentPolicy(): Promise<readonly string[]> {
  if (!attachmentPolicyPromise) {
    attachmentPolicyPromise = issueClient
      .getIssueAttachmentPolicy(new GetIssueAttachmentPolicyRequest({}))
      .then((response) => [...response.contentTypes])
      .catch((err) => {
        attachmentPolicyPromise = null;
        throw err;
      });
  }
  return attachmentPolicyPromise;
}

export async function getIssueAttachment(
  id: string,
): Promise<{ attachment: IssueAttachmentItem; content: Uint8Array }> {
  const response = await issueClient.getIssueAttachment(new GetIssueAttachmentRequest({ id }));
  if (!response.attachment) throw new Error("attachment not found");
  return { attachment: toAttachment(response.attachment), content: response.content };
}
